/* ── ManualBuilder ────────────────────────────────────────────
   Compliance Copy Builder — generates installation manual copy
   from the corpus based on product type, regions, and details.

   Phases:
     idle       → form input
     clarifying → user answers clarification questions
     classified → product classified, searching corpus
     generating → sections being generated
     complete   → all sections ready, export available
     error      → something went wrong

   Export: clipboard (per-section + all) + PDF via window.print()
   Styling: Otto design tokens (otto-tokens.css)
   ─────────────────────────────────────────────────────────── */

import { useCallback, useRef, useState } from 'react';
import type {
  ManualBuilderState,
  ManualSection,
} from '../types';
import {
  MANUAL_PRODUCT_CATEGORIES,
  MANUAL_TARGET_REGIONS,
  MANUAL_SECTION_LABELS,
} from '../types';
import { streamManualGenerate } from '../api';

/* ── Helpers ──────────────────────────────────────────────── */

function esc(text: string): string {
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Minimal markdown-like → text conversion for copy output */
function sectionBodyToText(body: string): string {
  return body.replace(/\\n/g, '\n');
}

function formatCitationLabel(c: { standard: string; clause: string }): string {
  return c.clause ? `${c.standard} cl. ${c.clause}` : c.standard;
}

const INITIAL_STATE: ManualBuilderState = {
  phase: 'idle',
  productType: '',
  targetRegions: ['AU'],
  productDetails: '',
  clarificationQuestions: [],
  clarificationAnswers: {},
  productName: '',
  domains: [],
  corpusHits: 0,
  sections: [],
  error: null,
};

/* ── Props ────────────────────────────────────────────────── */

interface Props {
  onClose: () => void;
}

/* ── Component ────────────────────────────────────────────── */

export default function ManualBuilder({ onClose }: Props) {
  const [state, setState] = useState<ManualBuilderState>(INITIAL_STATE);
  const [copiedSectionId, setCopiedSectionId] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  /* ── Helpers ─────────────────────────────────────────────── */

  const updateState = useCallback((patch: Partial<ManualBuilderState>) => {
    setState(prev => ({ ...prev, ...patch }));
  }, []);

  const handleClose = useCallback(() => {
    abortRef.current?.abort();
    onClose();
  }, [onClose]);

  /* ── Step 1: Submit form ──────────────────────────────────── */

  const handleSubmit = useCallback(async (clarificationAnswers?: Record<string, string>) => {
    if (!state.productType.trim()) return;
    if (state.targetRegions.length === 0) return;

    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    updateState({
      phase: 'classified',
      error: null,
      sections: [],
      clarificationQuestions: [],
      productName: '',
      domains: [],
      corpusHits: 0,
    });

    await streamManualGenerate(
      state.productType,
      state.targetRegions,
      state.productDetails,
      {
        onStatus: (phase, data) => {
          if (phase === 'classified') {
            updateState({
              phase: 'generating',
              productName: (data.product_name as string) || state.productType,
              domains: (data.domains as string[]) || [],
              corpusHits: (data.corpus_hits as number) || 0,
            });
          } else if (phase === 'generating') {
            updateState({ phase: 'generating' });
          }
        },
        onSection: (section) => {
          setState(prev => ({
            ...prev,
            sections: [...prev.sections, section],
          }));
        },
        onDone: (data) => {
          setState(prev => ({
            ...prev,
            phase: 'complete',
            productName: (data.product_name as string) || prev.productName,
            domains: (data.domains as string[]) || prev.domains,
          }));
        },
        onError: (message) => {
          updateState({ phase: 'error', error: message });
        },
        onClarificationNeeded: (questions) => {
          updateState({
            phase: 'clarifying',
            clarificationQuestions: questions,
            clarificationAnswers: {},
          });
        },
      },
      clarificationAnswers,
      ctrl.signal,
    );
  }, [state.productType, state.targetRegions, state.productDetails, updateState]);

  /* ── Step 2: Submit clarifications ───────────────────────── */

  const handleClarificationSubmit = useCallback(() => {
    handleSubmit(state.clarificationAnswers);
  }, [handleSubmit, state.clarificationAnswers]);

  /* ── Copy ────────────────────────────────────────────────── */

  const copySectionText = useCallback((section: ManualSection) => {
    const lines: string[] = [
      section.title.toUpperCase(),
      '',
      sectionBodyToText(section.body),
    ];
    if (section.citations.length > 0) {
      lines.push('', 'Sources:');
      section.citations.forEach(c => {
        lines.push(`  • ${formatCitationLabel(c)}${c.text ? `: ${c.text.slice(0, 120)}` : ''}`);
      });
    }
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopiedSectionId(section.section_id);
      setTimeout(() => setCopiedSectionId(null), 2000);
    }).catch(() => {});
  }, []);

  const copyAllText = useCallback(() => {
    const parts: string[] = [
      `INSTALLATION MANUAL — ${(state.productName || state.productType).toUpperCase()}`,
      `Generated: ${new Date().toLocaleDateString('en-AU')}`,
      '',
    ];
    state.sections.forEach((section, i) => {
      parts.push(`${'─'.repeat(60)}`);
      parts.push(`${i + 1}. ${section.title.toUpperCase()}`);
      parts.push(`[${section.normative_status.toUpperCase()}]`);
      parts.push('');
      parts.push(sectionBodyToText(section.body));
      if (section.citations.length > 0) {
        parts.push('', 'Sources:');
        section.citations.forEach(c => {
          parts.push(`  • ${formatCitationLabel(c)}${c.text ? `: ${c.text.slice(0, 100)}` : ''}`);
        });
      }
      parts.push('');
    });
    navigator.clipboard.writeText(parts.join('\n')).then(() => {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2500);
    }).catch(() => {});
  }, [state.sections, state.productName, state.productType]);

  /* ── PDF Export ──────────────────────────────────────────── */

  const handleExportPdf = useCallback(() => {
    const productLabel = esc(state.productName || state.productType);
    const dateStr = new Date().toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' });

    const sectionsHtml = state.sections.map((s, i) => {
      const normClass = s.normative_status === 'mandatory' ? 'badge-mandatory' : 'badge-recommended';
      const normLabel = s.normative_status === 'mandatory' ? 'MANDATORY' : 'RECOMMENDED';
      const bodyHtml = sectionBodyToText(s.body)
        .split('\n\n')
        .map(para => para.trim() ? `<p>${esc(para)}</p>` : '')
        .join('');
      const citationsHtml = s.citations.length > 0
        ? `<div class="citations"><div class="citations-label">Sources</div><ul>${
            s.citations.map(c => `<li><strong>${esc(formatCitationLabel(c))}</strong>${c.text ? ` — <em>${esc(c.text.slice(0, 160))}</em>` : ''}</li>`).join('')
          }</ul></div>`
        : '';

      return `
        <div class="section">
          <div class="section-header">
            <div class="section-num">${i + 1}</div>
            <h2 class="section-title">${esc(s.title)}</h2>
            <span class="badge ${normClass}">${normLabel}</span>
          </div>
          <div class="section-body">${bodyHtml}</div>
          ${citationsHtml}
        </div>`;
    }).join('');

    const tocHtml = state.sections.map((s, i) =>
      `<div class="toc-row"><span class="toc-num">${i + 1}.</span><span class="toc-title">${esc(s.title)}</span><span class="toc-dots"></span><span class="toc-badge ${s.normative_status === 'mandatory' ? 'badge-mandatory' : 'badge-recommended'}">${s.normative_status === 'mandatory' ? 'M' : 'R'}</span></div>`
    ).join('');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Compliance Manual — ${productLabel}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }

  /* Page setup */
  @page {
    size: A4;
    margin: 22mm 28mm 28mm 28mm;
    @bottom-center {
      content: "Compliance Hub  ·  " "${productLabel}";
      font-family: -apple-system, 'Helvetica Neue', Arial, sans-serif;
      font-size: 7.5pt;
      color: #a1a1ab;
    }
    @bottom-right {
      content: counter(page);
      font-family: -apple-system, 'Helvetica Neue', Arial, sans-serif;
      font-size: 7.5pt;
      color: #a1a1ab;
    }
  }
  @page :first { margin-top: 0; @bottom-center { content: none; } @bottom-right { content: none; } }

  /* Base */
  html { counter-reset: page 1; }
  body {
    font-family: -apple-system, 'Helvetica Neue', Arial, sans-serif;
    color: #18181b;
    background: #fff;
    font-size: 10.5pt;
    line-height: 1.65;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* Cover page */
  .cover {
    height: 297mm;
    display: flex;
    flex-direction: column;
    page-break-after: always;
    position: relative;
    overflow: hidden;
    margin: -22mm -28mm -28mm -28mm;
    padding: 0;
  }
  .cover-top {
    background: #0f172a;
    padding: 52px 52px 44px;
    flex: 0 0 auto;
  }
  .cover-eyebrow {
    font-size: 8.5pt;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #64748b;
    margin-bottom: 20px;
  }
  .cover-title {
    font-size: 26pt;
    font-weight: 700;
    color: #f8fafc;
    line-height: 1.2;
    margin-bottom: 10px;
    max-width: 420px;
  }
  .cover-subtitle {
    font-size: 14pt;
    font-weight: 400;
    color: #94a3b8;
    margin-bottom: 0;
    max-width: 400px;
  }
  .cover-accent { width: 48px; height: 3px; background: #3b82f6; margin: 24px 0 0; }
  .cover-body {
    background: #fff;
    padding: 40px 52px;
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .cover-meta { display: flex; flex-direction: column; gap: 10px; }
  .cover-meta-row { display: flex; gap: 16px; font-size: 10pt; }
  .cover-meta-label { color: #71717a; width: 110px; flex-shrink: 0; }
  .cover-meta-value { color: #18181b; font-weight: 500; }
  .cover-disclaimer {
    font-size: 8pt;
    color: #a1a1ab;
    line-height: 1.55;
    border-top: 1px solid #e4e4e7;
    padding-top: 14px;
    max-width: 100%;
  }

  /* TOC */
  .toc-page { page-break-after: always; padding-top: 8px; }
  .toc-heading { font-size: 13pt; font-weight: 700; color: #18181b; margin-bottom: 20px; padding-bottom: 8px; border-bottom: 2px solid #0f172a; }
  .toc-row { display: flex; align-items: baseline; gap: 6px; padding: 5px 0; border-bottom: 1px dotted #e4e4e7; font-size: 10pt; }
  .toc-num { color: #71717a; width: 20px; flex-shrink: 0; font-size: 9pt; }
  .toc-title { color: #18181b; flex: 1; }
  .toc-dots { flex: 0 0 20px; }
  .toc-badge { font-size: 7pt; font-weight: 700; padding: 1px 5px; border-radius: 2px; letter-spacing: 0.4px; flex-shrink: 0; }

  /* Sections */
  .sections-heading { font-size: 13pt; font-weight: 700; color: #18181b; margin-bottom: 28px; padding-bottom: 8px; border-bottom: 2px solid #0f172a; }
  .section { margin-bottom: 36px; page-break-inside: avoid; }
  .section-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e4e4e7;
  }
  .section-num {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #0f172a;
    color: #fff;
    font-size: 8pt;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  h2.section-title { font-size: 12pt; font-weight: 700; color: #18181b; flex: 1; }
  .badge { font-size: 7.5pt; font-weight: 700; padding: 2px 7px; border-radius: 3px; letter-spacing: 0.4px; white-space: nowrap; flex-shrink: 0; }
  .badge-mandatory { background: #fef2f2; color: #dc2626; border: 1px solid #fca5a5; }
  .badge-recommended { background: #f0fdf4; color: #15803d; border: 1px solid #86efac; }
  .section-body { font-size: 10.5pt; line-height: 1.65; color: #27272a; }
  .section-body p { margin-bottom: 10px; }
  .section-body p:last-child { margin-bottom: 0; }

  /* Citations */
  .citations {
    margin-top: 14px;
    font-size: 8.5pt;
    color: #52525b;
    background: #f8fafc;
    padding: 10px 14px;
    border-left: 3px solid #cbd5e1;
    border-radius: 0 4px 4px 0;
    page-break-inside: avoid;
  }
  .citations-label { font-weight: 700; color: #374151; margin-bottom: 6px; }
  .citations ul { padding-left: 14px; }
  .citations li { margin-bottom: 3px; line-height: 1.5; }
  .citations li strong { color: #374151; }

  /* Print overrides */
  @media print {
    body { font-size: 10.5pt; }
    .section { page-break-inside: avoid; }
    a { color: inherit; text-decoration: none; }
  }
</style>
</head>
<body>

  <!-- Cover -->
  <div class="cover">
    <div class="cover-top">
      <div class="cover-eyebrow">Product Compliance Documentation</div>
      <div class="cover-title">Installation &amp; Compliance Manual</div>
      <div class="cover-subtitle">${productLabel}</div>
      <div class="cover-accent"></div>
    </div>
    <div class="cover-body">
      <div class="cover-meta">
        <div class="cover-meta-row"><span class="cover-meta-label">Generated</span><span class="cover-meta-value">${dateStr}</span></div>
        <div class="cover-meta-row"><span class="cover-meta-label">Regions</span><span class="cover-meta-value">${esc(state.targetRegions.join(', '))}</span></div>
        ${state.domains.length > 0 ? `<div class="cover-meta-row"><span class="cover-meta-label">Standards</span><span class="cover-meta-value">${esc(state.domains.join(', '))}</span></div>` : ''}
        <div class="cover-meta-row"><span class="cover-meta-label">Sections</span><span class="cover-meta-value">${state.sections.length} (${state.sections.filter(s => s.normative_status === 'mandatory').length} mandatory, ${state.sections.filter(s => s.normative_status !== 'mandatory').length} recommended)</span></div>
      </div>
      <div class="cover-disclaimer">
        <strong>Important notice:</strong> This document is AI-generated from an Australian and New Zealand standards corpus.
        All requirements must be independently verified against current editions of the relevant standards before use in any compliance activity.
        This document does not constitute legal or professional compliance advice.
      </div>
    </div>
  </div>

  <!-- Table of Contents -->
  <div class="toc-page">
    <div class="toc-heading">Contents</div>
    ${tocHtml}
  </div>

  <!-- Sections -->
  <div class="sections-heading">Compliance Requirements</div>
  <main>${sectionsHtml}</main>

</body>
</html>`;

    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.onload = () => { win.focus(); win.print(); };
  }, [state.sections, state.productName, state.productType, state.targetRegions, state.domains]);

  /* ── Render ──────────────────────────────────────────────── */

  const isLoading = state.phase === 'classified' || state.phase === 'generating';
  const hasResults = state.sections.length > 0;

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>📋</span>
            <div>
              <div style={{ fontSize: 'var(--t-base)', fontWeight: 'var(--fw-emphasis)', color: 'var(--ink)' }}>
                Compliance Copy Builder
              </div>
              <div style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-4)', marginTop: '2px' }}>
                Generate installation manual copy from the compliance corpus
              </div>
            </div>
          </div>
          <button onClick={handleClose} style={closeButtonStyle} aria-label="Close">✕</button>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {/* Left: Input form */}
          <div style={leftPanelStyle}>
            <FormPanel
              state={state}
              onStateChange={updateState}
              onSubmit={() => handleSubmit()}
              isLoading={isLoading}
            />
          </div>

          {/* Right: Output */}
          <div style={rightPanelStyle}>
            {state.phase === 'idle' && (
              <EmptyState />
            )}
            {state.phase === 'clarifying' && (
              <ClarificationPanel
                questions={state.clarificationQuestions}
                answers={state.clarificationAnswers}
                onAnswerChange={(q, a) => setState(prev => ({
                  ...prev,
                  clarificationAnswers: { ...prev.clarificationAnswers, [q]: a },
                }))}
                onSubmit={handleClarificationSubmit}
              />
            )}
            {isLoading && (
              <LoadingPanel phase={state.phase} productName={state.productName || state.productType} />
            )}
            {(hasResults || state.phase === 'complete') && (
              <ResultsPanel
                state={state}
                onCopySection={copySectionText}
                onCopyAll={copyAllText}
                onExportPdf={handleExportPdf}
                copiedSectionId={copiedSectionId}
                copiedAll={copiedAll}
              />
            )}
            {state.phase === 'error' && (
              <ErrorPanel message={state.error || 'An unexpected error occurred.'} onRetry={() => handleSubmit()} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ─────────────────────────────────────────── */

function FormPanel({
  state,
  onStateChange,
  onSubmit,
  isLoading,
}: {
  state: ManualBuilderState;
  onStateChange: (patch: Partial<ManualBuilderState>) => void;
  onSubmit: () => void;
  isLoading: boolean;
}) {
  const canSubmit = state.productType.trim().length > 0 && state.targetRegions.length > 0 && !isLoading;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)', padding: 'var(--sp-5)' }}>
      {/* Product type */}
      <div>
        <label style={labelStyle}>Product Type *</label>
        <input
          type="text"
          placeholder="e.g. Mixer tap, RCD safety switch, Shower screen..."
          value={state.productType}
          onChange={e => onStateChange({ productType: e.target.value })}
          onKeyDown={e => e.key === 'Enter' && canSubmit && onSubmit()}
          style={inputStyle}
          list="product-categories"
        />
        <datalist id="product-categories">
          {MANUAL_PRODUCT_CATEGORIES.map(c => <option key={c} value={c} />)}
        </datalist>
      </div>

      {/* Target regions */}
      <div>
        <label style={labelStyle}>Target Regions *</label>
        <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
          {MANUAL_TARGET_REGIONS.map(r => {
            const isChecked = state.targetRegions.includes(r.id);
            return (
              <label key={r.id} style={checkboxLabelStyle(isChecked)}>
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={e => {
                    if (e.target.checked) {
                      onStateChange({ targetRegions: [...state.targetRegions, r.id] });
                    } else {
                      onStateChange({ targetRegions: state.targetRegions.filter(x => x !== r.id) });
                    }
                  }}
                  style={{ display: 'none' }}
                />
                {r.label}
              </label>
            );
          })}
        </div>
      </div>

      {/* Product details */}
      <div>
        <label style={labelStyle}>Product Details <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>(optional)</span></label>
        <textarea
          placeholder="Describe your product: materials, power source, intended use, special features..."
          value={state.productDetails}
          onChange={e => onStateChange({ productDetails: e.target.value })}
          rows={4}
          style={{ ...inputStyle, resize: 'vertical', minHeight: '90px' }}
        />
      </div>

      {/* Status/domain info */}
      {state.domains.length > 0 && (
        <div style={infoBoxStyle}>
          <div style={{ fontSize: 'var(--t-xs)', fontWeight: 'var(--fw-emphasis)', color: 'var(--ink-3)', marginBottom: '4px' }}>
            Applicable Standards Domains
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {state.domains.map(d => (
              <span key={d} style={domainBadgeStyle}>{d}</span>
            ))}
          </div>
          {state.corpusHits > 0 && (
            <div style={{ fontSize: 'var(--t-xs)', color: 'var(--ink-4)', marginTop: '6px' }}>
              {state.corpusHits} corpus clauses retrieved
            </div>
          )}
        </div>
      )}

      {/* Generate button */}
      <button
        onClick={onSubmit}
        disabled={!canSubmit}
        style={generateButtonStyle(canSubmit)}
      >
        {isLoading ? '⏳ Generating…' : '✨ Generate Manual Copy'}
      </button>

      {/* Disclaimer */}
      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--ink-5)', lineHeight: 1.5 }}>
        AI-generated from the compliance corpus. Verify all requirements against current standards before use.
      </div>
    </div>
  );
}

function ClarificationPanel({
  questions,
  answers,
  onAnswerChange,
  onSubmit,
}: {
  questions: string[];
  answers: Record<string, string>;
  onAnswerChange: (question: string, answer: string) => void;
  onSubmit: () => void;
}) {
  const allAnswered = questions.every(q => answers[q]?.trim());

  return (
    <div style={{ padding: 'var(--sp-5)', display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <div style={{ fontSize: 'var(--t-base)', fontWeight: 'var(--fw-emphasis)', color: 'var(--ink)' }}>
        🤔 A few quick questions
      </div>
      <div style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-3)' }}>
        Your product could fall under multiple standards. Please answer to get accurate copy.
      </div>
      {questions.map(q => (
        <div key={q}>
          <label style={labelStyle}>{q}</label>
          <input
            type="text"
            value={answers[q] || ''}
            onChange={e => onAnswerChange(q, e.target.value)}
            style={inputStyle}
            placeholder="Your answer…"
          />
        </div>
      ))}
      <button
        onClick={onSubmit}
        disabled={!allAnswered}
        style={generateButtonStyle(allAnswered)}
      >
        Continue →
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 'var(--sp-3)', color: 'var(--ink-4)', padding: 'var(--sp-6)' }}>
      <div style={{ fontSize: '40px', opacity: 0.4 }}>📄</div>
      <div style={{ fontSize: 'var(--t-base)', fontWeight: 'var(--fw-emphasis)', textAlign: 'center' }}>
        Your compliance copy will appear here
      </div>
      <div style={{ fontSize: 'var(--t-sm)', textAlign: 'center', maxWidth: '320px', lineHeight: 1.6 }}>
        Enter your product details, select target regions, and click Generate. 
        We'll sweep the corpus for every applicable requirement.
      </div>
    </div>
  );
}

function LoadingPanel({ phase, productName }: { phase: string; productName: string }) {
  const messages: Record<string, string[]> = {
    classified: [
      `Identified applicable standards…`,
      `Searching corpus across all domains…`,
      `Retrieving relevant clauses…`,
    ],
    generating: [
      `Generating compliance copy…`,
      `Writing safety warnings…`,
      `Building installation instructions…`,
      `Adding compliance markings…`,
    ],
  };
  const phaseMessages = messages[phase] || messages.generating;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 'var(--sp-4)', padding: 'var(--sp-6)' }}>
      <div style={{ fontSize: '32px' }}>⚙️</div>
      <div style={{ fontSize: 'var(--t-base)', fontWeight: 'var(--fw-emphasis)', color: 'var(--ink)', textAlign: 'center' }}>
        Building manual for {productName || 'your product'}…
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', width: '280px' }}>
        {phaseMessages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', fontSize: 'var(--t-sm)', color: 'var(--ink-3)' }}>
            <span style={{ fontSize: '12px', opacity: 0.8 }}>◉</span>
            {msg}
          </div>
        ))}
      </div>
      <div style={{ width: '200px', height: '3px', background: 'var(--line)', borderRadius: '2px', overflow: 'hidden', marginTop: 'var(--sp-2)' }}>
        <div style={{ height: '100%', background: 'var(--accent)', borderRadius: '2px', animation: 'manual-progress 2s ease-in-out infinite' }} />
      </div>
      <style>{`@keyframes manual-progress { 0%{width:10%} 50%{width:80%} 100%{width:10%} }`}</style>
    </div>
  );
}

function ResultsPanel({
  state,
  onCopySection,
  onCopyAll,
  onExportPdf,
  copiedSectionId,
  copiedAll,
}: {
  state: ManualBuilderState;
  onCopySection: (s: ManualSection) => void;
  onCopyAll: () => void;
  onExportPdf: () => void;
  copiedSectionId: string | null;
  copiedAll: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Results toolbar */}
      <div style={resultToolbarStyle}>
        <div style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-3)' }}>
          <strong style={{ color: 'var(--ink)' }}>{state.sections.length}</strong> sections
          {state.productName && <> for <strong style={{ color: 'var(--ink)' }}>{state.productName}</strong></>}
          {state.phase === 'generating' && <span style={{ color: 'var(--accent)', marginLeft: '8px' }}>⏳ generating…</span>}
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
          <button onClick={onCopyAll} style={toolbarButtonStyle} title="Copy all sections as plain text">
            {copiedAll ? '✅ Copied!' : '📋 Copy All'}
          </button>
          <button onClick={onExportPdf} style={toolbarButtonStyle} title="Export as PDF">
            📄 PDF
          </button>
        </div>
      </div>

      {/* Sections list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--sp-4) var(--sp-5)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          {state.sections.map((section) => (
            <SectionCard
              key={section.section_id}
              section={section}
              onCopy={() => onCopySection(section)}
              isCopied={copiedSectionId === section.section_id}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function SectionCard({
  section,
  onCopy,
  isCopied,
}: {
  section: ManualSection;
  onCopy: () => void;
  isCopied: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const isMandatory = section.normative_status === 'mandatory';
  const sectionLabel = MANUAL_SECTION_LABELS[section.section_id] || section.title;

  return (
    <div style={sectionCardStyle(isMandatory)}>
      {/* Card header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', marginBottom: expanded ? 'var(--sp-3)' : 0 }}>
        <button
          onClick={() => setExpanded(e => !e)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'var(--ink-4)', padding: '2px', lineHeight: 1 }}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? '▼' : '▶'}
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 'var(--t-sm)', fontWeight: 'var(--fw-emphasis)', color: 'var(--ink)' }}>
            {sectionLabel}
          </span>
        </div>
        <span style={normativeBadgeStyle(isMandatory)}>
          {isMandatory ? 'MANDATORY' : 'RECOMMENDED'}
        </span>
        <button onClick={onCopy} style={copyButtonStyle} title="Copy section">
          {isCopied ? '✅' : '📋'}
        </button>
      </div>

      {/* Card body */}
      {expanded && (
        <>
          <div style={sectionBodyStyle}>
            {sectionBodyToText(section.body).split('\n\n').map((para, i) => (
              para.trim() ? <p key={i} style={{ margin: '0 0 8px', lineHeight: 1.65 }}>{para}</p> : null
            ))}
          </div>

          {/* Citations */}
          {section.citations.length > 0 && (
            <div style={citationsStyle}>
              <div style={{ fontSize: 'var(--t-xs)', fontWeight: 'var(--fw-emphasis)', color: 'var(--ink-4)', marginBottom: '4px' }}>
                Sources
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {section.citations.map((c, ci) => (
                  <div key={ci} style={{ fontSize: 'var(--t-xs)', color: 'var(--ink-3)', display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                    <span style={{ color: 'var(--accent)', flexShrink: 0 }}>▸</span>
                    <span>
                      <strong>{formatCitationLabel(c)}</strong>
                      {c.text && <span style={{ color: 'var(--ink-4)' }}> — {c.text.slice(0, 120)}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ErrorPanel({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div style={{ padding: 'var(--sp-5)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--sp-4)', height: '100%', justifyContent: 'center' }}>
      <div style={{ fontSize: '32px' }}>⚠️</div>
      <div style={{ fontSize: 'var(--t-base)', fontWeight: 'var(--fw-emphasis)', color: 'var(--danger)', textAlign: 'center' }}>
        Generation failed
      </div>
      <div style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-3)', textAlign: 'center', maxWidth: '320px' }}>
        {message}
      </div>
      <button onClick={onRetry} style={generateButtonStyle(true)}>
        Retry
      </button>
    </div>
  );
}

/* ── Styles ─────────────────────────────────────────────────── */

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'var(--backdrop-std)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
};

const modalStyle: React.CSSProperties = {
  background: 'var(--bg-1)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--r-lg)',
  boxShadow: 'var(--shadow-lg)',
  width: '100%',
  maxWidth: '960px',
  height: '80vh',
  maxHeight: '720px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
  padding: '16px 20px',
  borderBottom: '1px solid var(--line)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
};

const closeButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '18px',
  color: 'var(--ink-4)',
  padding: '4px 6px',
  borderRadius: 'var(--r-sm)',
  lineHeight: 1,
};

const leftPanelStyle: React.CSSProperties = {
  width: '320px',
  flexShrink: 0,
  borderRight: '1px solid var(--line)',
  overflowY: 'auto',
};

const rightPanelStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--t-sm)',
  fontWeight: 'var(--fw-medium)',
  color: 'var(--ink-3)',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  background: 'var(--bg-2)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--r-md)',
  color: 'var(--ink)',
  fontSize: 'var(--t-sm)',
  outline: 'none',
};

const checkboxLabelStyle = (checked: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '5px 12px',
  borderRadius: 'var(--r-md)',
  border: `1px solid ${checked ? 'var(--accent)' : 'var(--line)'}`,
  background: checked ? 'var(--accent-soft)' : 'var(--bg-2)',
  color: checked ? 'var(--accent-text)' : 'var(--ink-3)',
  fontSize: 'var(--t-sm)',
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'all var(--dur-fast) var(--ease-default)',
});

const infoBoxStyle: React.CSSProperties = {
  background: 'var(--bg-2)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--r-md)',
  padding: '10px 12px',
};

const domainBadgeStyle: React.CSSProperties = {
  fontSize: 'var(--t-xs)',
  padding: '2px 8px',
  background: 'var(--accent-soft)',
  color: 'var(--accent-text)',
  borderRadius: 'var(--r-full)',
  border: '1px solid var(--accent-glow)',
};

const generateButtonStyle = (enabled: boolean): React.CSSProperties => ({
  width: '100%',
  padding: '10px 20px',
  background: enabled ? 'var(--accent)' : 'var(--bg-3)',
  color: enabled ? 'var(--accent-ink)' : 'var(--ink-5)',
  border: 'none',
  borderRadius: 'var(--r-md)',
  fontWeight: 'var(--fw-emphasis)',
  fontSize: 'var(--t-sm)',
  cursor: enabled ? 'pointer' : 'not-allowed',
  transition: 'background var(--dur-fast) var(--ease-default)',
});

const resultToolbarStyle: React.CSSProperties = {
  padding: '10px 20px',
  borderBottom: '1px solid var(--line)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  background: 'var(--bg-0)',
};

const toolbarButtonStyle: React.CSSProperties = {
  padding: '5px 12px',
  background: 'var(--bg-2)',
  border: '1px solid var(--line)',
  borderRadius: 'var(--r-md)',
  color: 'var(--ink-3)',
  fontSize: 'var(--t-sm)',
  cursor: 'pointer',
  transition: 'all var(--dur-fast)',
};

const sectionCardStyle = (mandatory: boolean): React.CSSProperties => ({
  background: 'var(--bg-2)',
  border: `1px solid ${mandatory ? 'var(--line-strong)' : 'var(--line)'}`,
  borderRadius: 'var(--r-md)',
  padding: '14px 16px',
  borderLeft: `3px solid ${mandatory ? 'var(--danger)' : 'var(--success)'}`,
});

const normativeBadgeStyle = (mandatory: boolean): React.CSSProperties => ({
  fontSize: '9px',
  fontWeight: 700,
  letterSpacing: '0.5px',
  padding: '2px 7px',
  borderRadius: 'var(--r-full)',
  background: mandatory ? 'var(--accent-critical-soft)' : 'rgba(22,163,74,0.12)',
  color: mandatory ? 'var(--accent-critical)' : 'var(--success)',
  border: `1px solid ${mandatory ? 'var(--accent-critical)' : 'var(--success)'}`,
  whiteSpace: 'nowrap' as const,
});

const copyButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  padding: '2px 4px',
  color: 'var(--ink-4)',
  flexShrink: 0,
};

const sectionBodyStyle: React.CSSProperties = {
  fontSize: 'var(--t-sm)',
  color: 'var(--ink-2)',
  lineHeight: 1.65,
  marginBottom: 'var(--sp-3)',
};

const citationsStyle: React.CSSProperties = {
  marginTop: '10px',
  padding: '8px 12px',
  background: 'var(--bg-0)',
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--line)',
};
